import { useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import UploadIcon from '@mui/icons-material/Upload'
import { useFinaliseUploadMutation, useGrantUploadMutation } from '../api/authoring'
import type { Document } from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { fileUrl, isImage, renditions } from '../model/derived'

/**
 * Putting a file into the platform, in three steps, none of which carries
 * a byte through the API.
 *
 * 1. **Ask.** `POST …/components/{id}/uploads` with the filename — the
 *    one thing about an upload that lives outside the object — and get
 *    back a presigned `PUT`, the key it will land at, and an expiry.
 * 2. **Send.** The browser `PUT`s straight to object storage. Nothing of
 *    ours runs, which is the whole argument: a request carrying a file is
 *    paid for twice, in invocation duration and in the payload ceiling
 *    that caps it, and neither buys anything.
 * 3. **Finalise.** `POST …/uploads/finalise` with the returned key. The
 *    platform reads what actually landed — content type, dimensions —
 *    generates the renditions and appends the revision itself.
 *
 * **Offered from the manifest, never from a type name.** That is why
 * this component knows nothing about Media: it is the same rule the
 * palette and the containment matrix follow, and a second type that
 * holds a file gets an uploader the day it is registered.
 *
 * The manifest's answer has two halves and a caller owes both. A type
 * `acceptsUpload`, and `uploadWhen` says which of its documents — a Media
 * pointing at somebody else's URL has no bytes of ours behind it, and a
 * grant against one is refused before a signature exists. Callers ask
 * `uploadsInto`, which is the two read together.
 *
 * **The intent that records the file is not on the wire and this client
 * cannot send it.** Every member of it — where the object landed, what it
 * is called, what it contains, how big it is — is something the platform
 * reads off the bytes, and a client claiming any of them could be
 * silently and permanently wrong on an immutable stream. Which is why
 * step 3's whole body is one key.
 */
export function BlobUploader({
  tenant,
  id,
  document,
  disabled,
}: {
  tenant: string
  /** The component the upload is scoped to — an upload has no other address. */
  id: string
  /** The component as it stands, for what it already holds. */
  document: Document | undefined
  disabled: boolean
}) {
  const [grant] = useGrantUploadMutation()
  const [finalise] = useFinaliseUploadMutation()
  const input = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<Step | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const busy = step !== null

  const send = async (file: File) => {
    setFailure(null)
    try {
      setStep('granting')
      const granted = await grant({ tenant, id, uploadRequest: { filename: file.name } }).unwrap()

      setStep('sending')
      // **A raw fetch, and deliberately not this client's base query.**
      // The signature is in the URL, so a request carrying one of our
      // credentials as well is refused by the store — the `Authorization`
      // header is part of what S3 checks against the signature it was
      // handed. Everything else in this client goes through a base query
      // that attaches a bearer token, and this is the one request that
      // must not.
      //
      // The content type is the browser's own reading of the file and is
      // sent only when it has one. It is not signed, so neither sending
      // nor omitting it is refused — and it is not believed either: the
      // finalise step reads the real type off the bytes, which is what
      // makes an image uploaded as `application/octet-stream` record as
      // the image it is.
      const stored = await fetch(granted.url, {
        method: 'PUT',
        body: file,
        headers: file.type ? { 'Content-Type': file.type } : undefined,
      })
      if (!stored.ok) {
        throw new Error(
          `The upload was refused (${stored.status}). Please try again.`,
        )
      }

      setStep('finalising')
      // One field. Everything else about the upload is a reading, and the
      // filename was stated once at the grant and has been carried in the
      // key ever since.
      await finalise({ tenant, id, finaliseUpload: { key: granted.key } }).unwrap()
    } catch (error) {
      setFailure(error instanceof Error ? error.message : problemMessage(error, 'The upload failed.'))
    } finally {
      setStep(null)
      // Cleared so that choosing the same file twice fires a change
      // event the second time — a re-upload of one file is an ordinary
      // thing to want after a failure.
      if (input.current) input.current.value = ''
    }
  }

  const url = fileUrl(document)
  const generated = renditions(document)
  const stored = (document?.fields ?? {}) as Record<string, unknown>
  const hasBlob = typeof stored.key === 'string' && stored.key !== ''

  return (
    <Stack spacing={1.5}>
      <Typography variant="h3">File</Typography>

      {hasBlob ? (
        <Stack spacing={1}>
          {url && isImage(document) && (
            <Box
              component="img"
              src={url}
              alt={typeof stored.alt === 'string' ? stored.alt : ''}
              sx={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', bgcolor: 'action.hover', borderRadius: 1 }}
            />
          )}
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {typeof stored.filename === 'string' && <Chip size="small" label={stored.filename} />}
            {typeof stored.contentType === 'string' && (
              <Chip size="small" variant="outlined" label={stored.contentType} />
            )}
            {typeof stored.width === 'number' && typeof stored.height === 'number' && stored.width > 0 && (
              <Chip size="small" variant="outlined" label={`${stored.width}×${stored.height}`} />
            )}
            {generated.map((rendition) => (
              <Chip key={rendition.url} size="small" variant="outlined" label={`${rendition.width}w`} />
            ))}
          </Stack>

          {/* Both of these are ordinary, and neither is an error — which
              is why they are said rather than left as an empty frame. */}
          {!url && (
            <Alert severity="info">
              The file is uploaded and safe, but it cannot be previewed here.
            </Alert>
          )}
          {url && generated.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No resized versions were made. Either the file is not an image, or it is already small.
            </Typography>
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No file yet. Upload one below.
        </Typography>
      )}

      {busy && (
        <Stack spacing={0.5}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            {STEP_LABEL[step]}
          </Typography>
        </Stack>
      )}

      {failure && <Alert severity="error">{failure}</Alert>}

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant={hasBlob ? 'outlined' : 'contained'}
          startIcon={<UploadIcon />}
          disabled={disabled || busy}
          onClick={() => input.current?.click()}
        >
          {hasBlob ? 'Replace file' : 'Upload a file'}
        </Button>
        <input
          ref={input}
          type="file"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void send(file)
          }}
        />
      </Stack>

      {hasBlob && (
        <Typography variant="caption" color="text.secondary">
          Replacing the file changes it everywhere it is used, once the site is next published. The previous file is
          kept, so what is already live is unaffected until then.
        </Typography>
      )}
    </Stack>
  )
}

type Step = 'granting' | 'sending' | 'finalising'

const STEP_LABEL: Record<Step, string> = {
  granting: 'Preparing the upload…',
  sending: 'Uploading…',
  finalising: 'Finishing up…',
}
